import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { Typography } from "../../../Components/Typography";
import Card from "../../../Components/Card";
import Button from "../../../Components/Button";
import { updateUser } from "../../../Utils/apiServices";
import { useAuth } from "../../../Context/authContext";

const SocialsTab = ({ initialFormData, onUpdate }) => {
  const { user, updateUser: updateAuthUser } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState({ type: "", text: "" });
  
  // Extract username from URL
  const extractUsername = (url, platform) => {
    if (!url) return "";
    try {
      if (platform === "twitter") {
        const match = url.match(/twitter\.com\/([^\/\?]+)/i) || url.match(/x\.com\/([^\/\?]+)/i);
        return match ? match[1] : url.replace(/^https?:\/\/(www\.)?(twitter\.com|x\.com)\/?/i, "").split("/")[0];
      } else if (platform === "facebook") {
        const match = url.match(/facebook\.com\/([^\/\?]+)/i);
        return match ? match[1] : url.replace(/^https?:\/\/(www\.)?facebook\.com\/?/i, "").split("/")[0];
      } else if (platform === "linkedin") {
        const match = url.match(/linkedin\.com\/company\/([^\/\?]+)/i) || url.match(/linkedin\.com\/in\/([^\/\?]+)/i);
        return match ? match[1] : url.replace(/^https?:\/\/(www\.)?linkedin\.com\/(company\/|in\/)?/i, "").split("/")[0];
      }
    } catch {
      return url;
    }
    return url;
  };

  const [socialData, setSocialData] = useState({
    twitter: "",
    facebook: "",
    linkedin: "",
    visibilityEveryone: false,
    visibilityMentees: false,
  });

  useEffect(() => {
    const twitterUrl = initialFormData?.x_url || user?.x_url || "";
    const facebookUrl = initialFormData?.facebook_url || user?.facebook_url || "";
    const linkedinUrl = initialFormData?.linkedin_url || user?.linkedin_url || "";
    const visibilityPrefs = user?.social_visibility || initialFormData?.social_visibility || {};

    setSocialData({
      twitter: extractUsername(twitterUrl, "twitter"),
      facebook: extractUsername(facebookUrl, "facebook"),
      linkedin: extractUsername(linkedinUrl, "linkedin"),
      visibilityEveryone: visibilityPrefs.everyone || false,
      visibilityMentees: visibilityPrefs.mentees || false,
    });
  }, [initialFormData, user]);

  const handleInputChange = (field, value) => {
    setSocialData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCheckboxChange = (field) => {
    setSocialData((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleSave = async () => {
    if (!user?.id) {
      setSaveMessage({ type: "error", text: "User ID not found. Please log in again." });
      return;
    }

    setIsSaving(true);
    setSaveMessage({ type: "", text: "" });

    try {
      // Build full URLs from usernames
      const xUrl = socialData.twitter ? `https://twitter.com/${socialData.twitter}` : null;
      const facebookUrl = socialData.facebook ? `https://facebook.com/${socialData.facebook}` : null;
      const linkedinUrl = socialData.linkedin ? `https://linkedin.com/in/${socialData.linkedin}` : null;

      const apiData = {
        x_url: xUrl,
        facebook_url: facebookUrl,
        linkedin_url: linkedinUrl,
        social_visibility: {
          everyone: socialData.visibilityEveryone,
          mentees: socialData.visibilityMentees,
        },
      };

      const response = await updateUser(user.id, apiData);

      if (response.success) {
        const updatedUserData = {
          x_url: xUrl,
          facebook_url: facebookUrl,
          linkedin_url: linkedinUrl,
          social_visibility: apiData.social_visibility,
        };
        
        updateAuthUser(updatedUserData);

        if (onUpdate) {
          onUpdate({
            ...initialFormData,
            x_url: xUrl,
            facebook_url: facebookUrl,
            linkedin_url: linkedinUrl,
            social_visibility: apiData.social_visibility,
          });
        }

        setSaveMessage({ type: "success", text: "Social profiles updated successfully!" });
        
        setTimeout(() => {
          setSaveMessage({ type: "", text: "" });
        }, 3000);
      } else {
        setSaveMessage({ 
          type: "error", 
          text: response.error || "Failed to update social profiles. Please try again." 
        });
      }
    } catch (error) {
      console.error("Error updating social profiles:", error);
      setSaveMessage({ 
        type: "error", 
        text: error.error || "An error occurred while updating your social profiles." 
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    const twitterUrl = initialFormData?.x_url || user?.x_url || "";
    const facebookUrl = initialFormData?.facebook_url || user?.facebook_url || "";
    const linkedinUrl = initialFormData?.linkedin_url || user?.linkedin_url || "";
    const visibilityPrefs = user?.social_visibility || initialFormData?.social_visibility || {};

    setSocialData({
      twitter: extractUsername(twitterUrl, "twitter"),
      facebook: extractUsername(facebookUrl, "facebook"),
      linkedin: extractUsername(linkedinUrl, "linkedin"),
      visibilityEveryone: visibilityPrefs.everyone || false,
      visibilityMentees: visibilityPrefs.mentees || false,
    });
    setSaveMessage({ type: "", text: "" });
  };

  return (
    <Card className="w-full">
      <div className="flex flex-col gap-6">
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-1">
            <Typography as="h2" className="text-lg font-bold text-primary-900 text-start">
              Socials
            </Typography>
            <Typography as="p" className="text-sm text-tertiary-600 text-start">
              Update your social network details here.
            </Typography>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={isSaving}
              loading={isSaving}
              className="bg-purple-600 hover:bg-purple-700 focus:ring-purple-500"
            >
              Save
            </Button>
          </div>
        </div>

        {saveMessage.text && (
          <div
            className={`p-4 rounded-lg border ${
              saveMessage.type === "success"
                ? "bg-green-50 border-green-200 text-green-800"
                : "bg-red-50 border-red-200 text-red-800"
            }`}
          >
            <div className="flex items-center gap-2">
              <Icon
                icon={
                  saveMessage.type === "success"
                    ? "lucide:check-circle"
                    : "lucide:alert-circle"
                }
                className={`h-5 w-5 ${
                  saveMessage.type === "success"
                    ? "text-green-600"
                    : "text-red-600"
                }`}
              />
              <Typography as="p" className="text-sm font-medium">
                {saveMessage.text}
              </Typography>
            </div>
          </div>
        )}

        {/* Social profiles section */}
        <div className="flex flex-row gap-6">
          <div className="flex flex-col gap-1">
            <Typography as="h3" className="text-base font-bold text-primary-900">
              Social profiles
            </Typography>
          </div>

          <div className="flex flex-col gap-4">
            {/* Twitter */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center">
                <span className="px-4 py-2.5 bg-slate-50 border border-r-0 border-slate-200 rounded-l-lg text-sm text-slate-600">
                  twitter.com/
                </span>
                <input
                  type="text"
                  value={socialData.twitter}
                  onChange={(e) => handleInputChange("twitter", e.target.value)}
                  placeholder="username"
                  className="flex-1 rounded-r-lg border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue"
                />
              </div>
            </div>

            {/* Facebook */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center">
                <span className="px-4 py-2.5 bg-slate-50 border border-r-0 border-slate-200 rounded-l-lg text-sm text-slate-600">
                  facebook.com/
                </span>
                <input
                  type="text"
                  value={socialData.facebook}
                  onChange={(e) => handleInputChange("facebook", e.target.value)}
                  placeholder="username"
                  className="flex-1 rounded-r-lg border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue"
                />
              </div>
            </div>

            {/* LinkedIn */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center">
                <span className="px-4 py-2.5 bg-slate-50 border border-r-0 border-slate-200 rounded-l-lg text-sm text-slate-600">
                  linkedin.com/in/
                </span>
                <input
                  type="text"
                  value={socialData.linkedin}
                  onChange={(e) => handleInputChange("linkedin", e.target.value)}
                  placeholder="company"
                  className="flex-1 rounded-r-lg border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Visibility section */}
        <div className="flex flex-row gap-6 pt-6 border-t border-slate-200">
          <div className="flex flex-col gap-1">
            <Typography as="h3" className="text-base font-bold text-primary-900 text-start">
              Visibility
            </Typography>
            <Typography as="p" className="text-sm text-tertiary-600 text-start">
              Make your social profiles visible
            </Typography>
          </div>

          <div className="flex flex-col gap-6">
            {/* Everyone checkbox */}
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={socialData.visibilityEveryone}
                onChange={() => handleCheckboxChange("visibilityEveryone")}
                className="mt-0.5 h-5 w-5 rounded border-slate-300 text-brand-blue focus:ring-brand-blue focus:ring-2 cursor-pointer"
              />
              <div className="flex flex-col gap-1 flex-1">
                <Typography as="span" className="text-sm font-semibold text-primary-900 text-start">
                  Everyone
                </Typography>
                <Typography as="span" className="text-sm text-tertiary-600 text-start">
                  Make my social profiles visible to everyone.
                </Typography>
              </div>
            </label>

            {/* Mentees checkbox */}
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={socialData.visibilityMentees}
                onChange={() => handleCheckboxChange("visibilityMentees")}
                className="mt-0.5 h-5 w-5 rounded border-slate-300 text-brand-blue focus:ring-brand-blue focus:ring-2 cursor-pointer"
              />
              <div className="flex flex-col gap-1 flex-1">
                <Typography as="span" className="text-sm font-semibold text-primary-900 text-start">
                  Mentees
                </Typography>
                <Typography as="span" className="text-sm text-tertiary-600 text-start">
                  Make my social profiles visible to mentees only.
                </Typography>
              </div>
            </label>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default SocialsTab;

